import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import {
  FlexibleWidthXYPlot,
  Hint,
  HorizontalBarSeries,
  HorizontalGridLines,
  VerticalBarSeries,
  VerticalGridLines,
  XAxis,
  XYPlot,
  YAxis,
} from 'react-vis';
import 'react-vis/dist/style.css';
import apiService from 'services/apiService';
import { colorVariant } from 'utils/colorTools';
import { translateKeyword } from 'utils/translate';

const SearchVisualizationBox = ({
  pageData,
  setIsLoading,
}) => {
  const lang = useSelector((store) => store.lang);
  // reference to the main wrapping Grid element for
  // listening to mouse move events
  const graphGridRef = useRef();

  const [boxData, setBoxData] = useState({});
  const [activeGraphIndex, setActiveGraphIndex] =
    useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // searching with keywords is case-sensitive
  // use both lowercase and capitalized words for greater coverage
  const combinedKeywords = pageData.keywords
    // capitalize the words (to make sure they all are)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    // copy the same words in lowercase
    .concat(
      pageData.keywords.map((word) => word.toLowerCase()),
    )
    .concat(pageData.sharedIgnore)
    // keyword filtering uses EI rather than NOT
    .map(
      (word) =>
        '&asiasanat=' + word.replaceAll('NOT', 'EI'),
    )
    .join('');

  useEffect(() => {
    const fetchVisualizationData = async () => {
      try {
        const fetchSearchParams =
          '?sort=date_asc&lang=fi' + combinedKeywords;

        const result = await apiService.fetchData(
          `search/all${fetchSearchParams}`,
        );

        if (
          typeof result.hits.total === 'object' &&
          'value' in result.hits.total
        ) {
          result.hits.total = result.hits.total.value;
        }

        return result;
      } catch (err) {
        console.error(err);
        return {};
      }
    };

    // async returns a Promise, so use then
    fetchVisualizationData()
      .then(setBoxData)
      .finally(() => setIsLoading(false));

    // cleanup on unmount
    return () => {
      setBoxData({});
    };
  }, [pageData]);

  // keep track of mouse position for tooltips
  useEffect(() => {
    if (
      graphGridRef === undefined ||
      graphGridRef.current === undefined
    )
      return;

    const setFromEvent = (e) =>
      setMousePos({ x: e.layerX, y: e.layerY });

    const { current } = graphGridRef;
    current.addEventListener('mousemove', setFromEvent);

    return () =>
      current.removeEventListener(
        'mousemove',
        setFromEvent,
      );
  }, [graphGridRef]);

  const reorganizeBucketData = (data, lang) => {
    if (data === undefined) return {};
    return Object.entries(data).reduce(
      (acc, [field, values]) => ({
        ...acc,
        [field]: values.buckets
          .map((bucket) => ({
            id: field,
            x: bucket.doc_count,
            y: translateKeyword(bucket.key, lang),
          }))
          .reverse(),
      }),
    );
  };

  const getHorizontalValues = (data, yearLimit) => {
    if (data === undefined || data.year === undefined)
      return [];

    // limit graph to only show data from the past x years
    const firstAllowedYear =
      new Date().getFullYear() - yearLimit;
    // create a object {year: count} to easily find year's count
    const yearToCountMap = data.year.reduce(
      (acc, year) => ({ ...acc, [year.y]: year.x }),
      {},
    );
    // create a list of yearLimit items (e.g. 20 + 1) and convert it
    // to objects of id, year (x) and search result count (y)
    return [...Array(yearLimit + 1)].map((_, i) => {
      const currentYear = firstAllowedYear + i;
      return {
        id: 'year',
        x: String(currentYear),
        y: yearToCountMap[currentYear] || 0,
      };
    });
  };

  const data = useMemo(
    () => reorganizeBucketData(boxData?.aggregations, lang),
    [boxData, lang],
  );

  // original data has x/y the wrong way around, so flip them around.
  // additionally limit the data to the past 20 years
  const years = useMemo(
    () => getHorizontalValues(data, 20),
    [data],
  );

  const chartsList = [
    <YearBarChart
      data={years}
      mousePos={mousePos}
      searchTerm={pageData.keywords.concat(
        pageData.sharedIgnore.map((term) =>
          term.replaceAll('NOT', 'EI'),
        ),
      )}
    />,
    <DocumentTypeGraph data={data} mousePos={mousePos} />,
  ];

  if (years?.length === undefined || years.length == 0)
    return <></>;

  return (
    <div ref={graphGridRef}>
      {/* list of different graphs, selected with activeGraphIndex */}
      {chartsList[activeGraphIndex]}
      <Grid container className="center-content" gap={1}>
        {/* list of buttons for changing the activeGraphIndex */}
        {['Asiakirjoja vuosittain', 'Dokumenttityypit'].map(
          (text, i) => (
            <Button
              className="graph-button"
              key={i}
              disabled={activeGraphIndex === i}
              disableTouchRipple
              variant="contained"
              onClick={() => setActiveGraphIndex(i)}
              sx={{
                backgroundColor: pageData.lightColor,
                '&:hover': {
                  backgroundColor: pageData.lightColor,
                },
              }}
            >
              {text}
            </Button>
          ),
        )}
      </Grid>
    </div>
  );
};

const YearBarChart = ({ data, mousePos, searchTerm }) => {
  const [animation, setAnimation] = useState(true);
  const [hintValue, setHintValue] = useState(null);

  const navigateUserToSearh = ({ x: searchYear }) => {
    const params = new URLSearchParams({
      filters: JSON.stringify({
        year: [searchYear],
        asiasanat: searchTerm,
      }),
    });

    window.location.href = `/?term=&${params}`;
  };

  // v = { id: "year", x: 2022, y: 222 } -> { 2022: 222 }
  // generated and displayed when hovering a bar
  const generateYearHint = (v) => ({ [v.x]: v.y });

  return (
    <FlexibleWidthXYPlot
      margin={{ left: 50, right: 10 }}
      height={230}
      xType="ordinal"
      stackBy="y"
    >
      <HorizontalGridLines />
      <VerticalBarSeries
        animation={animation}
        data={data}
        onValueMouseOver={(v) => {
          setHintValue(generateYearHint(v));
          setAnimation(false);
        }}
        onValueMouseOut={() => {
          setHintValue(null);
          setAnimation(true);
        }}
        onSeriesMouseOut={() => {
          setHintValue(null);
          setAnimation(true);
        }}
        onValueClick={navigateUserToSearh}
        color={'rgb(251, 197, 0)'}
        stroke={'black'}
        style={{ cursor: 'pointer' }}
      />
      <XAxis tickLabelAngle={-45} />
      <YAxis
        tickFormat={(val) =>
          Math.round(val) === val ? val : ''
        }
      />
      {hintValue && mousePos.x > 50 && (
        <Hint
          x={mousePos.x - 50}
          y={mousePos.y - 20}
          value={hintValue}
        />
      )}
    </FlexibleWidthXYPlot>
  );
};

const DocumentTypeGraph = ({ data, mousePos }) => {
  const [hintValue, setHintValue] = useState(null);

  return (
    <XYPlot
      margin={{ left: 130, right: 0 }}
      width={564}
      height={230}
      yType="ordinal"
    >
      <VerticalGridLines />
      <HorizontalBarSeries
        data={data['_index']}
        onValueMouseOver={(v) => {
          if (!hintValue || hintValue !== v) {
            setHintValue(v);
          }
        }}
        onValueMouseOut={() => setHintValue(null)}
        onSeriesMouseOut={() => setHintValue(null)}
        color={colorVariant(2)}
      />
      <XAxis
        tickFormat={(val) =>
          Math.round(val) === val ? val : ''
        }
        tickLabelAngle={-45}
      />
      <YAxis />
      {hintValue && (
        <Hint
          x={mousePos.x - 50}
          y={mousePos.y - 50}
          value={{
            [hintValue.y]: hintValue.x,
          }}
        />
      )}
    </XYPlot>
  );
};

export default SearchVisualizationBox;
